import type { Permission } from "@overbookd/permission";
import { isHttpError } from "~/utils/http/http-error.utils";
import { castMyUserInformationWithDate } from "~/utils/http/cast-date/user.utils";
import type { MyUserInformation, Profile } from "@overbookd/user";
import { UserRepository } from "~/repositories/user.repository";
import type { Membership } from "@overbookd/registration";
import { ADMIN } from "@overbookd/team-code";
import { ONE_SECOND_IN_MS } from "@overbookd/time";
import { ZitadelRepository } from "~/repositories/zitadel.repository";
import { registrationSteps, type RegistrationStepKey } from "@overbookd/http";
import { RegistrationRepository } from "~/repositories/registration/registration.repository";

type State = {
  loggedUser?: MyUserInformation;
  synced: boolean;
  fullyRegistered: boolean;
};

export const useMyStore = defineStore("authenticated-user", {
  state: (): State => ({
    loggedUser: undefined,
    synced: false,
    fullyRegistered: false,
  }),
  getters: {
    can:
      () =>
      (permission?: Permission): boolean => {
        const loggedUser = useMyStore().loggedUser;
        if (!loggedUser) return false;
        if (!permission) return true;

        const isAdmin = loggedUser.teams.includes(ADMIN);
        const hasPermission = loggedUser.permissions.includes(permission);
        return isAdmin || hasPermission;
      },

    isMemberOf:
      ({ loggedUser }) =>
      (team: string): boolean => {
        if (!loggedUser) return false;
        return (
          loggedUser.teams.includes(ADMIN) || loggedUser.teams.includes(team)
        );
      },
  },

  actions: {
    async fetchMyInformations() {
      const res = await UserRepository.getMyUser();
      if (isHttpError(res)) return;
      this.loggedUser = {
        ...this.loggedUser,
        ...castMyUserInformationWithDate(res),
      };
    },

    async sync() {
      const res = await UserRepository.userSync();
      this.synced = !isHttpError(res);
    },

    async checkRegistration(): Promise<RegistrationStepKey | undefined> {
      const res =
        await RegistrationRepository.checkAuthenticatedUserWithoutFormData();
      if (isHttpError(res)) return;

      if (res.next === registrationSteps.COMPLETED) {
        this.fullyRegistered = true;
      }
      return res.next;
    },

    clear() {
      setTimeout(() => {
        this.loggedUser = undefined;
        this.synced = false;
        this.fullyRegistered = false;
      }, ONE_SECOND_IN_MS);
    },

    async approveEndUserLicenceAgreement() {
      const res = await UserRepository.approveEndUserLicenceAgreement();
      if (isHttpError(res)) return;
      sendSuccessNotification("CGU approuvées ! 🎉");
      if (!this.loggedUser) return;
      this.loggedUser = { ...this.loggedUser, hasApprovedEULA: true };
    },

    async signVolunteerCharter() {
      const res = await UserRepository.signVolunteerCharter();
      if (isHttpError(res)) return;
      sendSuccessNotification("Charte bénévole signée ! 🎉");
      if (!this.loggedUser) return;
      this.loggedUser = { ...this.loggedUser, hasSignedVolunteerCharter: true };
    },

    async updateMyProfile(profile: Partial<Profile>) {
      const res = await UserRepository.updateMyProfile(profile);
      if (isHttpError(res)) return;
      sendSuccessNotification("Profil mis à jour ! 🎉");

      const updated = {
        ...this.loggedUser,
        ...castMyUserInformationWithDate(res),
      };
      this.loggedUser = updated;
    },

    async updateMyProfilePicture(profilePicture: FormData) {
      const res = await ZitadelRepository.updateProfilePicture(profilePicture);
      if (isHttpError(res)) return;
      sendSuccessNotification("Photo de profil mise à jour ! 🎉");

      await this.sync();
      await this.fetchMyInformations();
    },

    setLoggedUserMembershipApplication(application: Membership) {
      if (!this.loggedUser) return;
      this.loggedUser = {
        ...this.loggedUser,
        membershipApplication: application,
      };
    },
  },
});
